@import '../../styles/table.css';
@import '../../styles/control-elevation.css';
@import '../../utils/chrome-header.css';
@import '../../utils/control-density.css';
@import '../../utils/text-decoration.css';

:host {
  display: block;
  box-sizing: border-box;
  min-width: 0;
  max-width: 100%;
  container-type: inline-size;
  container-name: ds-table;
}

:host(.table-host--bounded) {
  min-block-size: 0;
}

:host(.table-host--fixed-height) {
  block-size: var(--_table-fixed-block-size);
}

:host(.table-host--viewport-fit) {
  block-size: var(--_table-viewport-fit-reserved-block-size, auto);
  min-block-size: var(--_table-viewport-fit-reserved-block-size, auto);
}

/*
 * The public table recipe stays at zero specificity for product-owned native
 * markup. Inside the component, these classes sit on ds-text hosts whose reset
 * intentionally owns display, padding, and border with higher specificity.
 * Reassert only those collided properties so component geometry cannot depend
 * on whether ds-text or ds-table happens to hydrate first.
 */
ds-text.ds-table__header-label-box {
  padding-inline: var(--dimension-space-025);
}

ds-text.ds-table__cell-track {
  display: flex;
}

.ds-table__cell--text-wrap ds-text.ds-table__cell-track--text,
.ds-table__cell--icon-text-wrap ds-text.ds-table__cell-track--text {
  display: block;
}

ds-text.ds-table__cell-track--text {
  padding-inline: var(--_table-cell-track-padding-inline);
}

ds-text.ds-table__cell-primary,
.ds-table__cell--empty ds-text.ds-table__cell-track {
  padding-block: var(--dimension-space-025);
}

/*
 * The public :where() wrap recipe has zero specificity, so ds-text's
 * variant line-height would win inside the component. Reassert on wrapping
 * cell layout classes so extra primary and secondary lines occupy later tracks.
 */
.ds-table__cell--text-wrap.ds-table__cell--text-single .ds-table__cell-primary,
.ds-table__cell--text-wrap.ds-table__cell--text-multi .ds-table__cell-primary,
.ds-table__cell--icon-text-wrap.ds-table__cell--icon-text-single .ds-table__cell-primary,
.ds-table__cell--icon-text-wrap.ds-table__cell--icon-text-multi .ds-table__cell-primary {
  line-height: var(--_table-wrap-primary-line-height);
  padding-block: var(--_table-wrap-primary-padding-block);
}

.ds-table__cell--text-wrap.ds-table__cell--text-multi .ds-table__cell-copy,
.ds-table__cell--icon-text-wrap.ds-table__cell--icon-text-multi .ds-table__cell-copy {
  gap: 0;
}

.ds-table__cell--text-wrap.ds-table__cell--text-multi .ds-table__cell-secondary,
.ds-table__cell--icon-text-wrap.ds-table__cell--icon-text-multi .ds-table__cell-secondary {
  line-height: var(--_table-wrap-secondary-line-height);
  padding-block: var(--_table-wrap-secondary-padding-block);
}

ds-text.ds-table__cell-secondary,
ds-text.ds-table__cell-tertiary {
  padding-block: var(--dimension-space-025);
}

.ds-table__cell--primary-text ds-text.ds-table__cell-secondary {
  padding-block: 0;
}

ds-text.ds-table__cell-run,
ds-text.ds-table__cell-run-separator {
  display: flex;
}

.ds-table__cell-tag-stack--tag-with-text ds-text.ds-table__cell-tag-text {
  padding-inline: var(--_table-cell-track-padding-inline);
  padding-block: var(--dimension-space-025);
}

.ds-table__cell-tag-stack--text-with-tag ds-text.ds-table__cell-tag-text {
  padding-inline: var(--_table-cell-track-padding-inline);
  padding-block: var(--dimension-space-025);
}

ds-text.ds-table__load-content {
  display: inline-flex;
}

ds-text.ds-table__bar-text {
  display: inline-flex;
  width: auto;
  max-width: 100%;
  padding-inline: var(--ds-control-label-inset, var(--dimension-space-025));
}

ds-text.ds-table__footer-summary {
  margin-inline-start: auto;
}

.ds-table__bar-trailing ds-pagination {
  max-inline-size: 100%;
}

.ds-table__bar-status slot::slotted(*) {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

ds-skeleton.ds-table__skeleton-image-shape {
  display: flex;
  width: 100%;
  height: 100%;
  --ds-control-height: 100%;
}

ds-skeleton.ds-table__skeleton-checkbox {
  --ds-control-height: var(--dimension-iconography-sm);
}

.ds-table__skeleton-checkbox-canvas {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  inline-size: var(--dimension-size-300);
  block-size: var(--dimension-size-300);
}

.ds-table__caption-customizer {
  position: relative;
  display: flex;
  align-items: center;
  min-inline-size: 0;
}

.ds-table__caption-customizer--loading > ds-button-unfilled {
  visibility: hidden;
}

.ds-table__caption-customizer > ds-skeleton {
  position: absolute;
  inset: 0;
}
